import React from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface RecognitionSectionProps {
  className?: string;
}

interface RecognitionItem {
  id: number;
  type: 'speaking' | 'media' | 'publication';
  title: string;
  organization: string;
  date: string;
  description: string;
  icon: string;
  image?: string;
  alt?: string;
}

const RecognitionSection = ({ className = '' }: RecognitionSectionProps) => {
  const recognitions: RecognitionItem[] = [
  {
    id: 1,
    type: 'speaking',
    title: 'Keynote: Human-Centered AI Leadership',
    organization: 'AI Transformation Summit London',
    date: 'October 2025',
    description: 'Delivered keynote address to 500+ enterprise leaders on bridging the gap between AI investment and organizational adoption.',
    icon: 'MicrophoneIcon',
    image: "https://images.unsplash.com/photo-1724827192883-95d0b50c81ce",
    alt: 'Conference speaker presenting to large audience in modern auditorium with stage lighting'
  },
  {
    id: 2,
    type: 'media',
    title: 'Featured in Financial Times',
    organization: 'Financial Times Technology Section',
    date: 'September 2025',
    description: 'Profiled as emerging leader in AI transformation consulting, highlighting our people-first methodology and proven results.',
    icon: 'NewspaperIcon',
    image: "https://images.unsplash.com/photo-1603460103154-86d929b2dfa5",
    alt: 'Financial Times newspaper folded on wooden desk with coffee cup'
  },
  {
    id: 3,
    type: 'publication',
    title: 'AI Change Leadership Framework',
    organization: 'Harvard Business Review',
    date: 'July 2025',
    description: 'Published comprehensive framework for AI transformation adopted by Fortune 500 companies globally.',
    icon: 'BookOpenIcon',
    image: "https://images.unsplash.com/photo-1582292277772-7382a9e81d1b",
    alt: 'Open business journal on desk with pen and reading glasses'
  },
  {
    id: 4,
    type: 'speaking',
    title: 'Panel: Change Management in AI Era',
    organization: 'European Change Management Conference',
    date: 'May 2025',
    description: 'Participated in expert panel discussing organizational readiness and capability building for AI adoption.',
    icon: 'UserGroupIcon',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_110662418-1767478423273.png",
    alt: 'Panel discussion with four professionals seated on stage at business conference'
  },
  {
    id: 5,
    type: 'media',
    title: 'Interview: The Future of AI Transformation',
    organization: 'Forbes Technology Council',
    date: 'March 2025',
    description: 'In-depth interview on the critical success factors for enterprise AI transformation and the role of change leadership.',
    icon: 'ChatBubbleLeftRightIcon',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_19a17a95c-1769166706712.png",
    alt: 'Professional interview setup with two people in modern office with city view'
  },
  {
    id: 6,
    type: 'publication',
    title: 'Research: AI Adoption Barriers',
    organization: 'MIT Sloan Management Review',
    date: 'January 2025',
    description: 'Co-authored research study identifying key organizational barriers to AI adoption and proven mitigation strategies.',
    icon: 'AcademicCapIcon',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_18dbc0db0-1766477275518.png",
    alt: 'Research papers and data charts spread on desk with laptop'
  }];


  const getTypeLabel = (type: string): string => {
    switch (type) {
      case 'speaking':
        return 'Speaking Engagement';
      case 'media':
        return 'Media Feature';
      case 'publication':
        return 'Publication';
      default:
        return 'Recognition';
    }
  };

  const getTypeColor = (type: string): string => {
    switch (type) {
      case 'speaking':
        return 'bg-accent/10 text-accent';
      case 'media':
        return 'bg-secondary/10 text-secondary';
      case 'publication':
        return 'bg-success/10 text-success';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <section className={`py-16 lg:py-24 bg-muted/30 ${className}`}>
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-headline text-primary mb-6">
            Industry Recognition
          </h2>
          <p className="text-lg text-muted-foreground font-body leading-relaxed">
            Our thought leadership and proven methodology have earned recognition from leading industry publications, conferences, and research institutions.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {recognitions.map((item) =>
          <div
            key={item.id}
            className="bg-card rounded-lg overflow-hidden shadow-card hover:shadow-strong transition-all duration-300 border border-border">

              {item.image &&
            <div className="relative h-48 overflow-hidden">
                  <AppImage
                src={item.image}
                alt={item.alt || ''}
                className="w-full h-full object-cover" />

                </div>
            }
              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold font-body ${getTypeColor(item.type)}`}>
                    {getTypeLabel(item.type)}
                  </span>
                  <div className="w-8 h-8 rounded-full bg-secondary/10 flex items-center justify-center">
                    <Icon name={item.icon as any} size={16} className="text-secondary" variant="solid" />
                  </div>
                </div>
                <h3 className="text-lg font-bold font-headline text-foreground mb-2">
                  {item.title}
                </h3>
                <p className="text-sm text-secondary font-semibold font-body mb-2">
                  {item.organization}
                </p>
                <p className="text-xs text-muted-foreground font-body mb-3">
                  {item.date}
                </p>
                <p className="text-muted-foreground font-body text-sm leading-relaxed">
                  {item.description}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

};

export default RecognitionSection;